import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { flushSync } from "react-dom";
import { useParams } from "react-router-dom";
import {
  getBusinessKycRequest,
  getShareholdersRequest,
  getShareholdingCoverageRequest,
  getPersonalKycRequest,
  getDocumentsListRequest, // ✅ new import
} from "@/services";

const EMPTY_COVERAGE = { coverage_errors: [], ubo_shareholding_threshold: null };

export function useKycData() {
  const { applicationId: applicationUuid } = useParams();
  const [businessKyc, setBusinessKyc] = useState(null);
  const [shareholdersList, setShareholdersList] = useState([]);
  const [shareholdersKyc, setShareholdersKyc] = useState([]);
  // The backend owns the shareholding rules; we only render what it reports.
  const [shareholdingCoverage, setShareholdingCoverage] = useState(EMPTY_COVERAGE);
  const [documentsList, setDocumentsList] = useState([]); // ✅ new state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [businessKycRefreshing, setBusinessKycRefreshing] = useState(false);
  const [shareholdersRefreshing, setShareholdersRefreshing] = useState(false);
  const [personalKycRefreshing, setPersonalKycRefreshing] = useState(false);

  // --- Business KYC ---
  const fetchBusinessKyc = useCallback(async () => {
    setBusinessKycRefreshing(true);
    try {
      const businessRes = await getBusinessKycRequest({ loan_id: applicationUuid });
      const row = (businessRes?.results || [])[0];
      flushSync(() => {
        setBusinessKyc(row);
        setBusinessKycRefreshing(false);
      });
      return row;
    } catch (err) {
      console.error("Error fetching Business KYC:", err);
      setError(err);
      flushSync(() => setBusinessKycRefreshing(false));
      return undefined;
    }
  }, [applicationUuid]);

  const reloadBusinessKyc = useCallback(fetchBusinessKyc, [fetchBusinessKyc]);

  // --- Shareholders ---
  // Coverage is derived from the shareholder rows, so the two always refresh together.
  const fetchShareholders = useCallback(async () => {
    setShareholdersRefreshing(true);
    try {
      const [shareholdersRes, coverageRes] = await Promise.all([
        getShareholdersRequest({ loan_id: applicationUuid }),
        getShareholdingCoverageRequest({ loan_id: applicationUuid }),
      ]);
      const list = shareholdersRes?.results || shareholdersRes || [];
      flushSync(() => {
        setShareholdersList(list);
        // Keep the last known coverage if the call failed, so a dropped request
        // cannot silently unblock the step.
        if (coverageRes?.results) setShareholdingCoverage(coverageRes.results);
        setShareholdersRefreshing(false);
      });
      return list;
    } catch (err) {
      console.error("Error fetching Shareholders:", err);
      setError(err);
      flushSync(() => setShareholdersRefreshing(false));
      return undefined;
    }
  }, [applicationUuid]);

  const reloadShareholders = useCallback(fetchShareholders, [fetchShareholders]);

  // --- Personal KYC ---
  const fetchPersonalKyc = useCallback(async () => {
    setPersonalKycRefreshing(true);
    try {
      const personalRes = await getPersonalKycRequest({ loan_id: applicationUuid });
      const rows = personalRes?.results || personalRes || [];
      flushSync(() => {
        setShareholdersKyc(rows);
        setPersonalKycRefreshing(false);
      });
      return rows;
    } catch (err) {
      console.error("Error fetching Personal KYC:", err);
      setError(err);
      flushSync(() => setPersonalKycRefreshing(false));
      return undefined;
    }
  }, [applicationUuid]);

  // Shareholder rows carry KYC-derived flags such as is_kyc_verified, so they go
  // stale as soon as a KYC is submitted or an admin verifies one.
  const reloadPersonalKyc = useCallback(async () => {
    const [rows] = await Promise.all([fetchPersonalKyc(), fetchShareholders()]);
    return rows;
  }, [fetchPersonalKyc, fetchShareholders]);

  // --- Documents ---
  const fetchDocumentsList = useCallback(async () => {
    try {
      const docsRes = await getDocumentsListRequest({ loan_id: applicationUuid });
      setDocumentsList(docsRes?.results || docsRes || []);
    } catch (err) {
      console.error("Error fetching Documents:", err);
      setError(err);
    }
  }, []);

  const reloadDocumentsList = useCallback(fetchDocumentsList, [fetchDocumentsList]);

  // --- Initial load ---
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const responses = await Promise.all([
        getBusinessKycRequest({ loan_id: applicationUuid }),
        getShareholdersRequest({ loan_id: applicationUuid }),
        getPersonalKycRequest({ loan_id: applicationUuid }),
        getDocumentsListRequest({ loan_id: applicationUuid }), // ✅ added here too
        getShareholdingCoverageRequest({ loan_id: applicationUuid }),
      ]);

      if (!isMountedRef.current) return;

      // apiRequest resolves with { success: false } instead of throwing
      const failed = responses.find((res) => res?.success === false);
      if (failed) {
        console.error("Error fetching KYC data:", failed.error);
        setLoadError(failed.error || true);
        return;
      }

      const [businessRes, shareholdersRes, personalRes, docsRes, coverageRes] = responses;
      setBusinessKyc((businessRes?.results || [])[0]);
      setShareholdersList(shareholdersRes?.results || shareholdersRes || []);
      setShareholdersKyc(personalRes?.results || personalRes || []);
      setDocumentsList(docsRes?.results || docsRes || []);
      setShareholdingCoverage(coverageRes?.results || EMPTY_COVERAGE);
    } catch (err) {
      if (isMountedRef.current) {
        console.error("Error fetching KYC data:", err);
        setLoadError(err || true);
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [applicationUuid]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // --- Merge shareholders with KYC ---
  const shareholdersWithKyc = useMemo(() => {
    if (!shareholdersList.length) return [];
    return shareholdersList.map((shareholder) => {
      const kyc = shareholdersKyc.find(
        (p) => String(p.shareholder) === String(shareholder.id)
      );
      return { ...shareholder, kyc };
    });
  }, [shareholdersList, shareholdersKyc]);

  return {
    businessKyc,
    shareholdersList,
    shareholdersKyc,
    shareholdersWithKyc, // 👈 merged result
    shareholdingCoverage,
    documentsList, // ✅ new return
    loading,
    businessKycRefreshing,
    shareholdersRefreshing,
    personalKycRefreshing,
    error,
    loadError,
    retryLoad: loadAll,
    reloadBusinessKyc,
    reloadShareholders,
    reloadPersonalKyc,
    reloadDocumentsList, // ✅ new method
  };
}
